import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import PageLayout from '@/components/Layout/PageLayout';
import type { RetryProps } from '@/components/retryFailedQueries';
import SomethingHappened from '@/components/SomethingHappened';
import type { ActivityLog, PenOverview, TaskList } from '@/domain/operationalDashboard';
import GridLayout from '@/routes/Client/Cage/pages/Reports/shared/GridLayout';

import { DashboardViewSwitcher } from '../components/DashboardViewSwitcher/DashboardViewSwitcher';

import AddActivityModal from './components/AddActivityModal/AddActivityModal';
import OperationalSidebar from './components/OperationalSidebar/OperationalSidebar';
import OperationalTasksPanel from './components/OperationalTasksPanel/OperationalTasksPanel';
import PenOverviewSection from './components/PenOverviewSection/PenOverviewSection';

interface IgnoreTaskParams {
    taskId: number;
    ignoredUntil?: Date;
    ignoredPermanently?: boolean;
}

interface CreateActivityParams {
    locationId: number;
    title: string;
    cageId?: number;
    comment?: string;
}

interface OperationalDashboardViewProps {
    locationName: string;
    locationId: number;
    taskList?: TaskList;
    activityLog?: ActivityLog;
    penOverview?: PenOverview;
    tasksLoading: boolean;
    activityLoading: boolean;
    penLoading: boolean;
    expectedPenCount: number;
    error: Error | null;
    /** Try again for the requests that failed. */
    retry?: RetryProps;
    onCompleteTask: (taskId: number) => void;
    onIgnoreTask: (params: IgnoreTaskParams) => void;
    onUnignoreTask: (taskId: number) => void;
    onAddActivity: (params: CreateActivityParams) => void;
}

export default function OperationalDashboardView(props: OperationalDashboardViewProps) {
    const {
        locationName,
        locationId,
        taskList,
        activityLog,
        penOverview,
        tasksLoading,
        activityLoading,
        penLoading,
        expectedPenCount,
        error,
        retry,
        onCompleteTask,
        onIgnoreTask,
        onUnignoreTask,
        onAddActivity,
    } = props;
    const { t } = useTranslation();
    const [addActivityOpen, setAddActivityOpen] = useState(false);

    if (error) {
        return (
            <PageLayout
                title={locationName}
                subtitle={t('operationalDashboard.title')}
                actions={<DashboardViewSwitcher />}>
                <SomethingHappened {...retry} />
            </PageLayout>
        );
    }

    return (
        <PageLayout
            title={locationName}
            subtitle={t('operationalDashboard.title')}
            actions={<DashboardViewSwitcher />}>
            <GridLayout contentLayout="2-1">
                <OperationalTasksPanel
                    taskList={taskList}
                    activityLog={activityLog}
                    tasksLoading={tasksLoading}
                    activityLoading={activityLoading}
                    onCompleteTask={onCompleteTask}
                    onIgnoreTask={onIgnoreTask}
                    onUnignoreTask={onUnignoreTask}
                    onAddActivityClick={() => setAddActivityOpen(true)}
                />
                <OperationalSidebar penOverview={penOverview} isLoading={penLoading} />
            </GridLayout>

            <GridLayout contentLayout="1">
                <PenOverviewSection
                    penOverview={penOverview}
                    isLoading={penLoading}
                    expectedPenCount={expectedPenCount}
                />
            </GridLayout>

            <AddActivityModal
                isOpen={addActivityOpen}
                locationId={locationId}
                pens={penOverview?.allPens ?? []}
                onClose={() => setAddActivityOpen(false)}
                onSubmit={onAddActivity}
            />
        </PageLayout>
    );
}
